import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Building2 } from "lucide-react";
import { listTenants } from "../services/tenant";
import type { ListTenantsResult, TenantStatus } from "../types/tenant";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { RequireRole } from "../components/RequireRole";
import { cn } from "../utils/cn";

const STATUS_CLS: Record<TenantStatus, { dot: string; label: string }> = {
  PROVISIONING: { dot: "bg-primary", label: "bg-primary-soft text-primary-soft-fg border-primary/20" },
  ACTIVE: { dot: "bg-success", label: "bg-success-soft text-success-soft-fg border-success/20" },
  ARCHIVED: { dot: "bg-muted-foreground", label: "bg-surface-2 text-muted-foreground border-border" },
};

const TIER_CLS: Record<string, string> = {
  free: "text-muted-foreground",
  starter: "text-primary-soft-fg",
  pro: "text-warning-soft-fg",
  enterprise: "text-danger-soft-fg",
};

export default function TenantList() {
  const [data, setData] = useState<ListTenantsResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await listTenants({ limit: 100 });
        if (alive) { setData(r); setErr(null); }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : "Erro ao listar tenants");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <>
      <PageHeader
        title="Tenants"
        subtitle={`${data?.pagination.total ?? "—"} tenant${data?.pagination.total === 1 ? "" : "s"} criado${data?.pagination.total === 1 ? "" : "s"} pelo WOS`}
        actions={
          <RequireRole anyOf={["platform-master", "platform-admin", "platform-operations", "dev-on-call"]}>
            <Link to="/onboardings/new">
              <Button size="sm">
                <Plus size={14} />
                Novo tenant
              </Button>
            </Link>
          </RequireRole>
        }
      />

      <div className="p-6">
        {err && <div className="p-3 mb-4 rounded-md bg-danger-soft text-danger-soft-fg text-sm">{err}</div>}
        {loading && <p className="text-sm text-muted-foreground">Carregando…</p>}

        {data && data.data.length === 0 && !loading && (
          <div className="rounded-md border border-dashed border-border bg-surface p-12 text-center">
            <Building2 size={32} className="mx-auto text-muted-foreground/60 mb-3" strokeWidth={1.5} />
            <p className="text-sm text-foreground font-medium">Nenhum tenant criado ainda</p>
            <p className="text-xs text-muted-foreground mt-1">Crie o primeiro tenant white label da Wynk.</p>
            <RequireRole anyOf={["platform-master", "platform-admin", "platform-operations", "dev-on-call"]}>
              <Link to="/onboardings/new" className="inline-block mt-4">
                <Button size="sm"><Plus size={14} /> Criar primeiro tenant</Button>
              </Link>
            </RequireRole>
          </div>
        )}

        {data && data.data.length > 0 && (
          <div className="rounded-md border border-border bg-surface overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface-1 text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left font-medium px-4 py-2 w-48">Tenant key</th>
                  <th className="text-left font-medium px-4 py-2">Nome</th>
                  <th className="text-left font-medium px-4 py-2 w-24">Tier</th>
                  <th className="text-left font-medium px-4 py-2 w-32">Status</th>
                  <th className="text-left font-medium px-4 py-2 w-32">Branding</th>
                  <th className="text-left font-medium px-4 py-2 w-44">Criado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-subtle">
                {data.data.map((t) => (
                  <tr key={t.tenantKey} className="hover:bg-surface-2 transition-colors cursor-pointer group">
                    <td className="px-4 py-2.5">
                      <Link to={`/tenants/${t.tenantKey}`} className="font-mono-tight text-xs text-foreground group-hover:underline">
                        {t.tenantKey}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-medium">
                      <Link to={`/tenants/${t.tenantKey}`} className="text-foreground group-hover:underline">
                        {t.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn("text-[11px] uppercase font-mono-tight tracking-wider font-medium", TIER_CLS[t.billingTier] ?? "text-muted-foreground")}>
                        {t.billingTier}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn(
                        "inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium font-mono-tight",
                        STATUS_CLS[t.status].label,
                      )}>
                        <span className={cn("w-1.5 h-1.5 rounded-full", STATUS_CLS[t.status].dot)} aria-hidden="true" />
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {t.brandingColor ? (
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block w-4 h-4 rounded border border-border-strong shrink-0"
                            style={{ backgroundColor: t.brandingColor }}
                            aria-hidden="true"
                          />
                          <code className="font-mono-tight text-xs text-muted-foreground">{t.brandingColor}</code>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground font-mono-tight text-xs">
                      {new Date(t.createdAt).toLocaleString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
